import { NgClass } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import {
  CustomerProfileData,
  ProcessRequest,
  QuickActionType,
  StatisticsCardData,
  StatisticsCardType,
  View360Response,
} from '@compiere-ws/models/apiz-domains';
import { CompiereDataFieldType, CompiereDataGridFilterType } from '@compiere-ws/models/compiere-data-json';
import { OperatorFilterType } from '@iupics-components/models/universal-filter';
import SpecificWindowUiComponent from '@iupics-components/specific/window/specific-window-ui/specific-window-ui.component';
import TextFieldUiComponent from '@iupics-components/standard/fields/text-field-ui/text-field-ui.component';
import {
  BadgeButtonUiComponent,
  QuickActionComponent,
  QuickActionData,
  QuickActionSubData,
  StatisticsCardUiComponent,
} from '@iupics-components/standard/ui';
import { BadgeButtonData } from '@iupics-components/standard/ui/badge-button-ui/badge-button-data';
import { IupicsField } from '@iupics-manager/models/iupics-data';
import { LogicEvaluator } from '@iupics-util/tools/logic-evaluator';
import { TranslateModule } from '@ngx-translate/core';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { View360DataService } from '../services/view-360-data.service';
import CustomerProfileComponent from './customer-profile/customer-profile.component';

@Component({
  selector: 'iu-customer-360',
  templateUrl: './customer-360.component.html',
  styleUrls: ['./customer-360.component.scss'],
  standalone: true,
  imports: [
    CustomerProfileComponent,
    BadgeButtonUiComponent,
    QuickActionComponent,
    StatisticsCardUiComponent,
    TextFieldUiComponent,
    NgClass,
    TranslateModule,
  ],
  providers: [View360DataService],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class Customer360Component extends SpecificWindowUiComponent implements OnInit {
  private BADGES_TABLE_NAMES = ['R_Request', 'C_Order', 'C_Payment', 'M_InOut', 'C_Invoice']; //! Order is important
  private STATS_COLUMN_NAMES = ['RV_YTD_Earnings', 'RV_MTD_Earnings', 'RV_Opportunities', 'RV_Offers'];
  private QUICK_ACTIONS_SUB_DATA_COLUMN_NAMES = ['C_DocType_ID', 'XX_ActionType_ID'];

  private readonly view360dataService = inject(View360DataService);

  /* Data */
  C_BPartner_ID: number;
  customer: CustomerProfileData;
  specificCustomerData: any;
  lastRefreshData: string;
  isSync = true;

  badges: BadgeButtonData[];
  statistics: StatisticsCardData[] = [];
  quickActions: QuickActionData[] = [];
  quickActionsProcess: QuickActionData[] = [];
  quickActionsSubActionsFields: IupicsField[] = [];
  textFields: { field: IupicsField; formatter: Intl.NumberFormat }[] = [];
  calculatedIndicator: { label: string; value: number; displayValue?: string };
  @ViewChild('calculatedIndicatorRef') calculatedIndicatorRef: TextFieldUiComponent;

  adLanguage: string;
  currencyFormatter: Intl.NumberFormat;

  // TODO: Fix data not updating on selected record in grid

  ngOnInit(): void {
    super.ngOnInit();
    // edit view refresh
    this.parentTab.dataUpdated.subscribe(() => this.getData(true));
    this.adLanguage = this.connectorService.getIupicsUserContext()['#AD_Language'].slice(0, 2);
    this.getData(true);
  }

  /* Data */
  getLoadingData(): void {
    this.customer = {
      id: -1,
      fullName: null,
      type: null,
      isForbidden: null,
      rating: {
        value: null,
      },
      profilePicture: null,
      top: null,
    };

    this.badges = [
      {
        label: this.translateService.instant('specificWindow.customer360.badgeActions'),
        value: null,
        rule: null,
      },
      {
        label: this.translateService.instant('specificWindow.customer360.badgeOrders'),
        value: null,
        rule: null,
      },
      {
        label: this.translateService.instant('specificWindow.customer360.badgePayments'),
        value: null,
        rule: null,
      },
      {
        label: this.translateService.instant('specificWindow.customer360.badgeExpeditions'),
        value: null,
        rule: null,
      },
      {
        label: this.translateService.instant('specificWindow.customer360.badgeInvoices'),
        value: null,
        rule: null,
      },
    ];
    this.statistics = [
      {
        type: StatisticsCardType.NORMAL,
        label: null,
        amount: null,
        pct: null,
      },
      {
        type: StatisticsCardType.NORMAL,
        label: null,
        amount: null,
        pct: null,
      },
      {
        type: StatisticsCardType.COUNT,
        label: null,
        amount: null,
        pct: null,
        count: null,
      },
      {
        type: StatisticsCardType.COUNT,
        label: null,
        amount: null,
        pct: null,
        count: null,
      },
    ];

    this.quickActions = [
      {
        // Opportunity
        type: QuickActionType.NEWRECORD,
        label: this.translateService.instant('specificWindow.customer360.quickOpportunity'),
        action: (): void => {
          this.zoomAcross('XX_Opportunity', {
            openNewRecord: true,
            zoomCtx: {
              C_BPartner_ID: this.C_BPartner_ID,
            },
          });
        },
      },
      {
        // Action
        type: QuickActionType.NEWRECORD,
        label: this.translateService.instant('specificWindow.customer360.quickAction'),
        subActionsDataQuery: {
          columnName: 'XX_ActionType_ID',
        },
        action: (): void => {
          this.zoomAcross('R_Request', {
            openNewRecord: true,
            zoomCtx: {
              C_BPartner_ID: this.C_BPartner_ID,
            },
          });
        },
      },
      {
        // Command
        type: QuickActionType.NEWRECORD,
        label: this.translateService.instant('specificWindow.customer360.quickCommand'),
        subActionsDataQuery: {
          columnName: 'C_DocType_ID',
          context: {
            IsSOTrx: 'Y',
            IsReturnTrx: 'N',
            IsReleaseDocument: 'N',
            isQuote: 'N',
          },
        },
        action: (target: QuickActionSubData): void => {
          this.zoomAcross('C_Order', {
            openNewRecord: true,
            zoomCtx: {
              C_BPartner_ID: this.C_BPartner_ID,
              C_DocTypeTarget_ID: target.id,
            },
          });
        },
      },
      {
        // Offer
        type: QuickActionType.NEWRECORD,
        label: this.translateService.instant('specificWindow.customer360.quickOffer'),
        action: (): void => {
          this.zoomAcross('C_RfQ', {
            openNewRecord: true,
            zoomCtx: {
              C_BPartner_ID: this.C_BPartner_ID,
            },
          });
        },
      },
    ];
    this.quickActionsProcess = [
      {
        // Refresh
        type: QuickActionType.PROCESS,
        label: this.translateService.instant('specificWindow.customer360.quickRefreshData'),
        readonly: false,
      },
    ];
    this.calculatedIndicator = {
      label: this.translateService.instant('specificWindow.customer360.calculatedIndicator'),
      value: null,
    };
  }

  getData(refresh = false): void {
    if (refresh) {
      this.getLoadingData();
    }

    const { data: dataStored } = this.parentTab.dataStored;

    this.setSpecificData();

    this.customer = {
      id: dataStored?.C_BPartner_ID ?? -1,
      fullName: dataStored.Name,
      profilePicture: dataStored?.ZCreditMessage ?? '',
      type: 'client',
      isForbidden: dataStored?.SOCreditStatus?.id === 'S',
      rating: dataStored?.RatingPct ?? 0,
      top: -1,
    };

    if (!dataStored?.['C_BPartner_ID']) {
      return;
    }

    this.C_BPartner_ID = dataStored['C_BPartner_ID'];

    this.view360dataService.getCustomer360Details(this.C_BPartner_ID).subscribe((res) => {
      this.customer.top = res.top;
      this.setStatistics(res);
      this.setCalculatedField(res);
      this.setBadgeButtonsData(res);
      this.setCurrencyFormatter(res);
      this.setFields();
      this.setQuickActions();
      this.setLastRefreshData(res.lastRefresh);
    });
  }

  setSpecificData(): void {
    this.specificCustomerData = this.fields[0];
    this.quickActionsProcess[0].processId = this.fields.find(
      (f) => f.data.columnName === 'refresh_rv_customer_360_views'
    )?.processId;
  }

  setFields(): void {
    this.textFields = []; // reset textfields

    for (const field of this.fields.filter(
      (f) => !['RV_Customer_360', 'refresh_rv_customer_360_views'].includes(f.data.columnName)
    )) {
      if (this.STATS_COLUMN_NAMES.includes(field.data.columnName)) {
        const index = this.STATS_COLUMN_NAMES.indexOf(field.data.columnName);
        this.statistics[index] = {
          ...this.statistics[index],
          label: field.data.label,
        };
      } else if (this.QUICK_ACTIONS_SUB_DATA_COLUMN_NAMES.includes(field.data.columnName)) {
        this.quickActionsSubActionsFields.push(field);
      } else if (!this.STATS_COLUMN_NAMES.includes(field.data.columnName)) {
        this.textFields.push({
          formatter: field.data.columnName === 'FlatDiscount' ? undefined : this.currencyFormatter,
          field,
        });
      }
    }
  }

  setStatistics(data: View360Response): void {
    this.statistics = [
      // 'RV_YTD_Earnings'
      {
        ...this.statistics[0],
        ...data.ytdEarnings,
      },
      // 'RV_MTD_Earnings'
      {
        ...this.statistics[1],
        ...data.mtdEarnings,
      },
      // 'RV_Opportunities'
      {
        ...this.statistics[2],
        ...data.opportunities,
      },
      // 'RV_Offers'
      {
        ...this.statistics[3],
        ...data.offers,
      },
    ];
  }

  setCalculatedField(data: View360Response): void {
    const daysFormatter = Intl.NumberFormat(this.adLanguage);

    this.calculatedIndicator = {
      ...this.calculatedIndicator,
      value: data?.paymentAverageDays ?? 0,
      displayValue: `${daysFormatter.format(data?.paymentAverageDays ?? 0)} ${this.translateService.instant(
        'calendar.unit.day'
      )}`,
    };

    this.calculatedIndicatorRef.initTooltip({
      description: this.specificCustomerData.data.description,
      help: this.specificCustomerData.data.help,
    });
  }

  setCurrencyFormatter(data: View360Response): void {
    const currency = data?.currency ?? this.connectorService.getIupicsUserContext()['$CurrencyISO'];
    this.currencyFormatter = Intl.NumberFormat(this.adLanguage, { currency, style: 'currency' });
  }

  setBadgeButtonsData(data: View360Response): void {
    if (!data) {
      this.badges = this.badges.map((badge) => {
        return {
          ...badge,
          rule: 1,
          value: 0,
        };
      });
      return;
    }

    this.badges = [
      {
        // Actions
        ...this.badges[0],
        value: data?.actionsBadge.value ?? 0,
        // rule: data?.requestafterendtime > 0 ? -1 : data?.requestcloseendtime > 0 ? 0 : 1,
        rule: data?.actionsBadge.rule,
      },
      {
        // Orders
        ...this.badges[1],
        value: data?.ordersBadge.value ?? 0,
        // rule: data?.ordernotbilled > 0 || data?.orderafterdatepromised > 0 ? -1 : 1,
        rule: data?.ordersBadge.rule,
      },
      {
        // Payments
        ...this.badges[2],
        value: data?.paymentsBadge.value ?? 0,
        // rule: data?.paymentnotallocatedafterduedate > 0 ? -1 : data?.paymentnotallocatedbeforeduedate > 0 ? 0 : 1,
        rule: data?.paymentsBadge.rule,
      },
      {
        // Expeditions
        ...this.badges[3],
        value: data?.expeditionsBadge.value ?? 0,
        // rule: data?.expeditionopenafterdatepromised > 0 ? -1 : 1,
        rule: data?.expeditionsBadge.rule,
      },
      {
        // Invoices
        ...this.badges[4],
        value: data?.invoicesBadge.value ?? 0,
        // rule: data?.invoicenotpaidafterduedate > 0 ? -1 : data?.invoicenotpaidbeforeduedate > 0 ? 0 : 1,
        rule: data?.invoicesBadge.rule,
      },
    ];
  }

  setQuickActions(): void {
    for (const field of this.quickActionsSubActionsFields) {
      const index = this.quickActions.findIndex(
        (qa: QuickActionData) => qa?.subActionsDataQuery?.columnName === field.data?.columnName
      );
      let validationCode = null;
      if (field.data?.validationCode) {
        const context = this.quickActions?.[index]?.subActionsDataQuery.context ?? {};
        validationCode = LogicEvaluator.replaceVariables(
          field.data.validationCode,
          this.connectorService.getIupicsUserContext(),
          context
        );
      }

      this.parentTab.store
        .getAutocompleteData(CompiereDataFieldType.FORM_ITEM, field.formDetailItemId, true, null, validationCode)
        .subscribe((dataWs) => {
          this.quickActions[index].subActions = dataWs ?? [];
        });
    }
  }

  setLastRefreshData(date: Date): void {
    if (!date) {
      return;
    }

    this.lastRefreshData = new Intl.DateTimeFormat(this.adLanguage, { timeStyle: 'short', dateStyle: 'long' }).format(
      date
    );
  }

  doQuickActionProcess(data: QuickActionData): void {
    const ADProcessId = data.processId ?? -1;
    if (ADProcessId === -1) {
      return;
    }

    const params: ProcessRequest = {
      ad_process_id: ADProcessId,
      className: null,
      record_id: null,
      tableName: null,
      tables: null,
      params: {},
      windowCtx: this.getCurrentContext(),
      ad_tab_id: null,
    };

    const me = this.connectorService.getIupicsUserAccount();
    const qap = this.quickActionsProcess.find((p) => p.processId === ADProcessId);
    if (!qap) {
      return;
    }

    qap.readonly = true;
    const processSub = this.processService.executeProcess(params).subscribe(() => {
      const progressSub = this.progressService.watchProcessInProgress().subscribe((pings) => {
        const ping = pings.find((p) => p.AD_User_ID.id === me.id && p.AD_Process_ID.id === ADProcessId);
        if (ping && (ping.Status === 'finish' || ping.Status === 'cancel')) {
          this.isSync = false;
          qap.readonly = false;
          processSub.unsubscribe();
          progressSub.unsubscribe();
        }
      });
    });
  }

  syncData(): void {
    this.getData(true);
    this.isSync = true;
  }

  /* Zoom Across */
  zoomAcross(
    tableName: string,
    options: { record_id?: any; dataGridRequest?: any; openNewRecord?: boolean; zoomCtx?: any }
  ): void {
    // const isSOTrx = this.parentTab?.dataStored?.data?.IsSOTrx === 'Y' ?? undefined;
    this.subscriptions.push(
      this.uiCreator.zoomAcross(tableName, tableName + '_ID', options?.record_id ?? null, true).subscribe({
        next: (dataWs) => {
          if (dataWs && dataWs.length > 0) {
            this.zoomInfo = {
              // tableName: this.data.detailZoom.tableName,
              windowId: dataWs[0].Window_ID,
              dataUUID: dataWs[dataWs.length - 1]['Record_ID'],
              record_id: options?.record_id ?? null,
              children: dataWs.length > 1 ? dataWs.splice(0, dataWs.length - 1) : null,
              isInCurrentWindow: true,
            };

            const source = {
              id: null,
              menuType: IupicsMenuType.WINDOW,
            };

            if (options?.record_id) {
              source.id = dataWs[0]?.Record_ID ?? null;
            }

            if (options?.openNewRecord) {
              source.id = 'newRecord';
            }
            this.urlParamsService.updateUrlParams({ initGridParams: { request: options?.dataGridRequest ?? null } });
            this.workspaceService.openTargetSearchEmt.emit({
              zoomInfo: this.zoomInfo,
              cat: { id: parseInt(dataWs[0].Window_ID, 10) },
              source,
              ctx: options?.zoomCtx ?? null,
            });
          }
        },
      })
    );
  }

  badgeButtonClick(index: number): void {
    const tableName = this.BADGES_TABLE_NAMES[index];
    if (tableName) {
      const dataGridRequest = {
        filterModel: {
          C_BPartner_ID: {
            filterType: CompiereDataGridFilterType.SET,
            operators: [OperatorFilterType.EQUALS],
            values: [this.parentTab.dataStored.data['C_BPartner_ID']],
          },
        },
      };

      switch (index) {
        case 0: // Actions
          dataGridRequest.filterModel['R_Status_ID'] = {
            filterType: CompiereDataGridFilterType.SET,
            operators: [OperatorFilterType.EQUALS],
            values: [1000204],
          };
          break;
        case 1: // Orders
          dataGridRequest.filterModel['DocStatus'] = {
            filterType: CompiereDataGridFilterType.SET,
            operators: [OperatorFilterType.NOT_EQUALS],
            values: [['IN', 'VO', 'CL', 'DR']],
          };
          break;
        case 2: // Payments
          dataGridRequest.filterModel['IsAllocated'] = {
            filterType: CompiereDataGridFilterType.TEXT,
            operators: [OperatorFilterType.NOT_EQUALS],
            values: ['Y'],
          };
          break;
        case 3: // Expeditions
          dataGridRequest.filterModel['DocStatus'] = {
            filterType: CompiereDataGridFilterType.SET,
            operators: [OperatorFilterType.EQUALS],
            values: ['IP'],
          };
          break;
        case 4: // Invoices
          dataGridRequest.filterModel['IsPaid'] = {
            filterType: CompiereDataGridFilterType.TEXT,
            operators: [OperatorFilterType.NOT_EQUALS],
            values: ['Y'],
          };
          break;
        default:
          break;
      }

      this.zoomAcross(tableName, { dataGridRequest });
    }
  }

  goToEditCustomer(): void {
    this.zoomAcross('C_BPartner', { record_id: this.parentTab.dataStored.data['C_BPartner_ID'] });
  }
}
